<script setup lang="ts">
  defineOptions({
    name: 'ChatWindow',
  })

  import { computed, nextTick, onBeforeUnmount, ref, type PropType, toRef } from 'vue'

  import { notifyError } from '@common/core-layer/utils/toast.notify'

  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

  import { useGroups } from '@communities/groups-layer/composables/useGroups'

  import DragDropZone from '@common/shared-ui-layer/components/DragDropZone.vue'
  import { useI18n } from 'vue-i18n'
  import { useChats } from '../../composables/useChats'

  import { useConversationActions } from '../../composables/useConversationActions'

  import { ConversationEventType } from '../../types/chats.enums'

  import ChatConversationActionConfirmations from '../molecules/ChatConversationActionConfirmations.vue'

  import ChatConversationBlockedNotice from '../molecules/ChatConversationBlockedNotice.vue'

  import ChatMessageEditor from '../molecules/ChatMessageEditor.vue'

  import ChatMessageList from '../molecules/ChatMessageList.vue'

  import ChatUserIntro from '../molecules/ChatUserIntro.vue'

  import ChatWindowHeader from '../molecules/ChatWindowHeader.vue'

  import { useChatParticipant } from '../../composables/useChatParticipant'
  import type { IChatAttachment, IConversation, IMessage } from '../../types/conversation.types'
  import type { RecordedVoiceMessage } from '../../types/chat-message-editor.types'

  const props = defineProps({
    conversation: {
      type: Object as PropType<IConversation>,
      required: true,
    },
    isMinimized: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits<{
    toggleMinimize: []
    close: []
  }>()

  // Composables
  const chats = useChats()
  const { getConversationRecipient } = useChatParticipant()

  const messageListRef = ref<InstanceType<typeof ChatMessageList> | null>(null)
  const editorRef = ref<InstanceType<typeof ChatMessageEditor> | null>(null)

  const { t } = useI18n()

  const { CPNavigateTo } = useCPNavigation()

  const { store: groupsStore } = useGroups()

  // Fetch messages for this conversation
  const conversationId = toRef(() => props.conversation._id)

  const messagesQuery = chats.fetchMessages(conversationId)

  const getMessageKey = (message: IMessage) => {
    return message._id || message.uniqueRefId || `${message.sender}-${message.createdAt}`
  }

  // Get flattened messages from paginated response (deduped)
  const messages = computed(() => {
    const pages = messagesQuery.data.value?.pages || []
    const seen = new Set<string>()
    const result: IMessage[] = []

    for (const page of pages) {
      for (const message of page.messages) {
        const key = getMessageKey(message)
        if (seen.has(key)) continue
        seen.add(key)
        result.push(message)
      }
    }

    return result
  })

  const recipient = computed(() => getConversationRecipient(props.conversation))

  // Check if conversation has messages (from API or embedded in conversation)
  const hasMessages = computed(() => {
    return messages.value.length > 0 || (props.conversation?.messages?.length || 0) > 0
  })

  // Combined messages (API + embedded)
  const allMessages = computed(() => {
    if (messages.value.length > 0) {
      return messages.value
    }
    return props.conversation?.messages || []
  })

  // Check if there are more messages to load
  const hasMoreMessages = computed(() => {
    return messagesQuery.hasNextPage?.value ?? false
  })

  const locationId = computed(() => {
    return groupsStore.currentGroup?.locationId || props.conversation.locationId || ''
  })

  const groupId = computed(() => {
    return groupsStore.currentGroup?._id || props.conversation.groupId || ''
  })

  // Handle load more messages (infinite scroll)
  const handleLoadMore = () => {
    if (messagesQuery.hasNextPage.value && !messagesQuery.isFetchingNextPage.value) {
      messagesQuery.fetchNextPage()
    }
  }

  const { isBlockedByCurrentUser, blockConversation, unblockConversation, deleteConversation } = useConversationActions(
    toRef(props, 'conversation'),
    conversationId,
    {
      onCleared: () => {
        if (props.conversation?._id) {
          chats.store.closeChatWindow(props.conversation._id)
        }
      },
    }
  )

  const activeConfirmation = ref<'block' | 'delete' | null>(null)

  const handleRequestBlock = () => {
    activeConfirmation.value = 'block'
  }

  const handleRequestDelete = () => {
    activeConfirmation.value = 'delete'
  }

  const handleConfirmBlock = async () => {
    await blockConversation()
  }

  const handleConfirmDelete = async () => {
    await deleteConversation()
  }

  const handleViewProfile = async () => {
    const userSlug = recipient.value?.slug?.trim()
    if (!userSlug) {
      return
    }

    try {
      await CPNavigateTo({
        name: 'communities-user',
        params: { userSlug },
      })
    } catch (error) {
      console.error('Failed to navigate to user profile:', error)
    }
  }

  const clearTypingTimeout = () => {
    if (!typingTimeout.value) {
      return
    }

    clearTimeout(typingTimeout.value)
    typingTimeout.value = null
  }

  const setTypingInactive = () => {
    hasSentTyping.value = false
    clearTypingTimeout()
  }

  const sendConversationEventSafely = async (eventType: ConversationEventType) => {
    try {
      await chats.sendConversationEvent(props.conversation, eventType)
      return true
    } catch (error) {
      console.error(`Failed to send conversation event (${eventType}):`, error)
      return false
    }
  }

  // Handle send message
  const handleSendMessage = async (payload: { text: string; html: string; attachments: IChatAttachment[] }) => {
    if (!props.conversation._id) return

    try {
      // Guard early: if conversation/recipient ids are not resolvable, sending must be blocked.
      if (
        !chats.buildSendMessageInput({
          conversationId: props.conversation._id,
          recipientId: recipient.value?.recipientId,
        })
      ) {
        return
      }

      for (const attachment of payload.attachments) {
        const mediaType = attachment.type === 'document' ? 'file' : attachment.type
        // Build each send payload from the latest recipient state to avoid stale ids.
        const sendInput = chats.buildSendMessageInput({
          conversationId: props.conversation._id,
          recipientId: recipient.value?.recipientId,
          media: {
            type: mediaType,
            url: attachment.url,
            licenseId: attachment.licenseId,
            fileName: attachment.name,
            fileSize: attachment.size,
            mimeType: attachment.mimeType,
            width: attachment.width,
            height: attachment.height,
            // Voice-note metadata (audio only).
            kind: attachment.kind,
            durationSec: attachment.durationSec,
            waveformPeaks: attachment.waveformPeaks,
          },
        })
        if (!sendInput) return
        await chats.sendMessage(sendInput)
      }

      const messageText = payload.text.trim()
      if (messageText) {
        const sendInput = chats.buildSendMessageInput({
          conversationId: props.conversation._id,
          recipientId: recipient.value?.recipientId,
          text: payload.html || messageText,
        })
        if (!sendInput) return
        await chats.sendMessage(sendInput)
      }

      await sendConversationEventSafely(ConversationEventType.NOT_TYPING)
      setTypingInactive()

      editorRef.value?.resetForm?.()

      nextTick(() => {
        messageListRef.value?.scrollToBottom()
      })

      try {
        await chats.markAsRead(props.conversation._id)
      } catch (error) {
        console.error('Failed to mark conversation as read:', error)
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      notifyError(t('chats.messageStatus.failed'), error instanceof Error ? error.message : t('chats.messageStatus.failed'))
    }
  }

  // Handle a recorded voice note: the optimistic bubble + upload + send all live
  // in useChats, so this just forwards the clip and scrolls to the new bubble.
  const handleSendVoiceMessage = async (recorded: RecordedVoiceMessage) => {
    try {
      await chats.sendVoiceMessage({
        conversationId: props.conversation._id,
        recipientId: recipient.value?.recipientId,
        ...recorded,
      })
      nextTick(() => {
        messageListRef.value?.scrollToBottom()
      })
    } catch (error) {
      console.error('Failed to send voice message:', error)
      notifyError(t('chats.messageStatus.failed'), error instanceof Error ? error.message : t('chats.messageStatus.failed'))
    }
  }

  // Handle send GIF (sends immediately without attachments)
  const handleSendGif = async (gif: { url: string; title: string }) => {
    if (!props.conversation._id) return

    try {
      const sendInput = chats.buildSendMessageInput({
        conversationId: props.conversation._id,
        recipientId: recipient.value?.recipientId,
        media: {
          type: 'gif',
          url: gif.url,
          fileName: gif.title,
        },
      })
      if (!sendInput) return

      await chats.sendMessage(sendInput)

      await sendConversationEventSafely(ConversationEventType.NOT_TYPING)
      setTypingInactive()

      editorRef.value?.resetForm?.()

      nextTick(() => {
        messageListRef.value?.scrollToBottom()
      })

      try {
        await chats.markAsRead(props.conversation._id)
      } catch (error) {
        console.error('Failed to mark conversation as read:', error)
      }
    } catch (error) {
      console.error('Failed to send GIF:', error)
      notifyError(t('chats.messageStatus.failed'), error instanceof Error ? error.message : t('chats.messageStatus.failed'))
    }
  }

  const typingTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

  const hasSentTyping = ref(false)

  const handleTyping = async () => {
    if (!props.conversation?._id) {
      return
    }

    if (!hasSentTyping.value) {
      const hasSentTypingEvent = await sendConversationEventSafely(ConversationEventType.TYPING)
      hasSentTyping.value = hasSentTypingEvent
    }

    clearTypingTimeout()

    typingTimeout.value = setTimeout(async () => {
      await sendConversationEventSafely(ConversationEventType.NOT_TYPING)
      setTypingInactive()
    }, 10000)
  }

  onBeforeUnmount(() => {
    clearTypingTimeout()

    if (props.conversation?._id) {
      void sendConversationEventSafely(ConversationEventType.NOT_TYPING)
      hasSentTyping.value = false
    }
  })
</script>

<template>
  <div class="chat-window flex flex-col overflow-hidden bg-surface transition-all duration-200" :class="[isMinimized ? 'h-auto' : '']">
    <!-- Header -->
    <ChatWindowHeader
      :recipient="recipient"
      :is-minimized="isMinimized"
      :is-blocked="isBlockedByCurrentUser"
      @toggle-minimize="emit('toggleMinimize')"
      @view-profile="handleViewProfile"
      @block-conversation="handleRequestBlock"
      @unblock-conversation="unblockConversation"
      @delete-conversation="handleRequestDelete"
      @close="emit('close')"
    />

    <!-- Content (hidden when minimized) -->
    <DragDropZone
      v-if="!isMinimized"
      variant="sm"
      class="flex flex-1 flex-col overflow-hidden"
      :validation="editorRef?.validations"
      :overlay-message="t('chats.messageEditor.dropFiles')"
      @filesDropped="editorRef?.handleFilesDropped($event)"
      @validationError="editorRef?.handleValidationError($event)"
    >
      <!-- Empty State: Show user intro when no messages -->
      <template v-if="!hasMessages">
        <div class="custom-scrollbar flex flex-1 flex-col overflow-y-auto">
          <ChatUserIntro :recipient="recipient" />
        </div>
      </template>

      <!-- Messages List: Show when there are messages -->
      <ChatMessageList
        v-else
        ref="messageListRef"
        class="custom-scrollbar"
        :messages="allMessages"
        :recipient="recipient"
        :is-loading-more="messagesQuery.isFetchingNextPage.value"
        :has-more-messages="hasMoreMessages"
        @load-more="handleLoadMore"
      />

      <ChatConversationBlockedNotice
        v-if="isBlockedByCurrentUser"
        :recipient-name="recipient?.fullName || ''"
        :id-prefix="`chat-blocked-${conversationId}`"
        @unblock="unblockConversation"
      />

      <!-- Message Editor -->
      <ChatMessageEditor
        ref="editorRef"
        :location-id="locationId"
        :group-id="groupId"
        :disabled="isBlockedByCurrentUser"
        use-external-drag-drop
        @send="handleSendMessage"
        @send-gif="handleSendGif"
        @voice-message="handleSendVoiceMessage"
        @typing="handleTyping"
      />
    </DragDropZone>

    <ChatConversationActionConfirmations
      :active-confirmation="activeConfirmation"
      :recipient-name="recipient?.fullName || ''"
      @update:active-confirmation="activeConfirmation = $event"
      @confirm-block="handleConfirmBlock"
      @confirm-delete="handleConfirmDelete"
    />
  </div>
</template>

<style scoped>
  .chat-window {
    width: 400px;
    height: 600px;
    min-height: 600px;
    max-height: 600px;
    border-radius: 8px 8px 0 0;
    border: 1px solid var(--cp-border-500);
    box-shadow: 0 32px 64px -12px var(--cp-shadow-strong);
  }

  .chat-window.h-auto {
    height: auto;
    min-height: auto;
    max-height: auto;
  }

  /* Custom thin scrollbar (3px) */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: var(--cp-font-300) transparent;
  }

  .custom-scrollbar::-webkit-scrollbar {
    width: 3px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: var(--cp-font-300);
    border-radius: 2px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: var(--cp-font-400);
  }
</style>
